import { useEffect, useMemo, useState, useCallback } from 'react';
import { TrendingUp, TrendingDown, Minus, Download, Target, AlertTriangle } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../utils/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const fmtHrs = (h) => {
  if (h === null || h === undefined) return '—';
  const sign = h < 0 ? '-' : '';
  const v = Math.abs(h);
  if (v < 0.01) return `${sign}0h`;
  return `${sign}${v.toFixed(2)}h`;
};
const fmtPct = (p) => p === null || p === undefined ? '—' : `${p > 0 ? '+' : ''}${p}%`;
const toIST = (d) => {
  const ist = new Date(d.getTime() + (5.5 * 60 - d.getTimezoneOffset()) * 60000);
  return ist.toISOString().slice(0, 10);
};
const today = () => toIST(new Date());
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return toIST(d); };

// Color a variance % — green = good (came in at/under estimate), amber close, red over
const varianceColor = (pct) => {
  if (pct === null || pct === undefined) return 'text-slate-400';
  if (pct <= 0) return 'text-emerald-700';
  if (pct <= 25) return 'text-amber-700';
  return 'text-red-700';
};
const varianceBg = (pct) => {
  if (pct === null || pct === undefined) return 'bg-slate-50';
  if (pct <= 0) return 'bg-emerald-50';
  if (pct <= 25) return 'bg-amber-50';
  return 'bg-red-50';
};
const VarianceIcon = ({ pct }) => {
  if (pct === null || pct === undefined) return <Minus className="h-3 w-3" />;
  if (pct < -5) return <TrendingDown className="h-3 w-3" />;
  if (pct > 5) return <TrendingUp className="h-3 w-3" />;
  return <Minus className="h-3 w-3" />;
};

export default function VarianceReport() {
  const [fromDate, setFromDate] = useState(daysAgo(89));
  const [toDate, setToDate] = useState(today());
  const [statusFilter, setStatusFilter] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState('tasks'); // tasks | assignee | client | service

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from_date: fromDate, to_date: toDate };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await api.get('/reports/variance', { params });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load variance report');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totals = data?.totals;

  const exportCsv = () => {
    if (!data) return;
    const rows = groupBy === 'tasks' ? data.tasks :
                 groupBy === 'assignee' ? data.by_assignee :
                 groupBy === 'client' ? data.by_client : data.by_service;
    if (!rows?.length) return;
    let head, lines;
    if (groupBy === 'tasks') {
      head = ['Task', 'Client', 'Service', 'Assignee', 'Status', 'Estimate (h)', 'Actual (h)', 'Variance (h)', 'Variance %'];
      lines = rows.map(r => [r.title, r.client_name, r.service_name, r.assignee_name, r.status, r.estimate_hours, r.actual_hours, r.variance_hours, r.variance_pct].join(','));
    } else {
      head = [groupBy[0].toUpperCase() + groupBy.slice(1), 'Tasks', 'Estimate (h)', 'Actual (h)', 'Variance (h)', 'Variance %'];
      lines = rows.map(r => [r.label, r.tasks, r.estimate_hours, r.actual_hours, r.variance_hours, r.variance_pct].join(','));
    }
    const blob = new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `variance_${fromDate}_to_${toDate}_${groupBy}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 md:p-8 space-y-5" data-testid="variance-report-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" data-testid="variance-heading">
            <Target className="h-7 w-7 text-[#5B6FD6]" /> Estimate vs Actual
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            For every task that has an <span className="font-semibold">Estimate</span> set, see how the logged hours compare. Negative variance = came in under (great); large positive variance = scope creep or under-quoting.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data?.tasks?.length} data-testid="variance-export-btn">
          <Download className="h-4 w-4 mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 p-4 bg-white">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">From</label>
            <Input type="date" value={fromDate} max={toDate} onChange={e => setFromDate(e.target.value)} className="bg-white w-40" data-testid="variance-from" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">To</label>
            <Input type="date" value={toDate} min={fromDate} max={today()} onChange={e => setToDate(e.target.value)} className="bg-white w-40" data-testid="variance-to" />
          </div>
          <div className="w-44">
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-white" data-testid="variance-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="completed">Completed only</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-48">
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">View</label>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger className="bg-white" data-testid="variance-groupby"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tasks">Per task</SelectItem>
                <SelectItem value="assignee">Aggregate by assignee</SelectItem>
                <SelectItem value="client">Aggregate by client</SelectItem>
                <SelectItem value="service">Aggregate by service</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Totals strip */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="variance-totals">
          <Tile label="Tasks with estimate" value={data.total_tasks} />
          <Tile label="Total estimate" value={fmtHrs(totals.estimate_hours)} color="text-slate-700" />
          <Tile label="Total actual" value={fmtHrs(totals.actual_hours)} color="text-slate-700" />
          <Tile
            label="Net variance"
            value={`${fmtHrs(totals.variance_hours)} · ${fmtPct(totals.variance_pct)}`}
            color={varianceColor(totals.variance_pct)}
            bg={varianceBg(totals.variance_pct)}
          />
        </div>
      )}

      {/* Body */}
      <Card className="border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-10 text-center text-slate-400">Loading…</div>
          ) : data?.total_tasks === 0 ? (
            <div className="p-12 text-center">
              <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-3" />
              <p className="text-slate-700 font-semibold">No tasks with estimates in this range.</p>
              <p className="text-slate-500 text-sm mt-1">Edit a task and set its "Estimate (hours)" field to start tracking variance.</p>
            </div>
          ) : groupBy === 'tasks' ? (
            <table className="w-full text-sm" data-testid="variance-table-tasks">
              <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Task</th>
                  <th className="text-left px-4 py-3 font-semibold">Client</th>
                  <th className="text-left px-4 py-3 font-semibold">Assignee</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-right px-4 py-3 font-semibold">Estimate</th>
                  <th className="text-right px-4 py-3 font-semibold">Actual</th>
                  <th className="text-right px-4 py-3 font-semibold">Variance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.tasks.map(t => (
                  <tr key={t.task_id} className="hover:bg-slate-50/50" data-testid={`variance-row-${t.task_id}`}>
                    <td className="px-4 py-3"><Link to={`/tasks/${t.task_id}`} className="font-medium text-slate-800 hover:text-[#5B6FD6]">{t.title}</Link></td>
                    <td className="px-4 py-3 text-slate-700">{t.client_name}</td>
                    <td className="px-4 py-3 text-slate-700">{t.assignee_name}</td>
                    <td className="px-4 py-3"><span className="text-xs text-slate-500 capitalize">{t.status.replace(/_/g, ' ')}</span></td>
                    <td className="px-4 py-3 text-right font-mono">{fmtHrs(t.estimate_hours)}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmtHrs(t.actual_hours)}</td>
                    <td className={`px-4 py-3 text-right font-mono font-semibold ${varianceColor(t.variance_pct)}`}>
                      <span className="inline-flex items-center gap-1 justify-end">
                        <VarianceIcon pct={t.variance_pct} />
                        {fmtHrs(t.variance_hours)} ({fmtPct(t.variance_pct)})
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm" data-testid={`variance-table-${groupBy}`}>
              <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">{groupBy === 'assignee' ? 'Assignee' : groupBy === 'client' ? 'Client' : 'Service'}</th>
                  <th className="text-right px-4 py-3 font-semibold">Tasks</th>
                  <th className="text-right px-4 py-3 font-semibold">Estimate</th>
                  <th className="text-right px-4 py-3 font-semibold">Actual</th>
                  <th className="text-right px-4 py-3 font-semibold">Variance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(groupBy === 'assignee' ? data.by_assignee : groupBy === 'client' ? data.by_client : data.by_service).map(r => (
                  <tr key={r.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-medium text-slate-800">{r.label}</td>
                    <td className="px-4 py-3 text-right font-mono">{r.tasks}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmtHrs(r.estimate_hours)}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmtHrs(r.actual_hours)}</td>
                    <td className={`px-4 py-3 text-right font-mono font-semibold ${varianceColor(r.variance_pct)}`}>
                      <span className="inline-flex items-center gap-1 justify-end">
                        <VarianceIcon pct={r.variance_pct} />
                        {fmtHrs(r.variance_hours)} ({fmtPct(r.variance_pct)})
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}

const Tile = ({ label, value, color = 'text-slate-700', bg = 'bg-slate-50' }) => (
  <div className={`rounded-lg px-3 py-2.5 ${bg}`}>
    <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">{label}</p>
    <p className={`text-lg font-mono font-semibold ${color}`}>{value}</p>
  </div>
);
